import React from "react";
import { ITEMS, evaluate } from "./glasgowRules";

const SEVERITY_CARD = {
    normal: "from-emerald-900 to-emerald-700",
    mild: "from-emerald-900 to-emerald-700",
    moderate: "from-amber-800 to-amber-600",
    severe: "from-red-900 to-red-700",
};

function OptionButton({ selected, onClick, badge, label, detail }) {
    const cls = selected
        ? "bg-slate-800 hover:bg-slate-700 text-white shadow-md"
        : "bg-white hover:bg-stone-50 text-gray-900";
    return (
        <button type="button" onClick={onClick} aria-pressed={selected}
            className={`w-full flex items-center gap-3 text-left rounded-lg px-3 py-2 focus:outline-none focus:ring focus:ring-slate-300 ${cls}`}>
            <span className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold ${selected ? "bg-white text-slate-800" : "bg-stone-100"}`}>{badge}</span>
            <span>
                <span className="block font-semibold text-sm sm:text-base">{label}</span>
                {detail && <span className={`block text-xs ${selected ? "text-slate-200" : "text-gray-600"}`}>{detail}</span>}
            </span>
        </button>
    );
}

export default function Glasgow() {
    const [values, setValues] = React.useState({});
    const r = evaluate(values);
    const set = (key, v) => setValues((prev) => ({ ...prev, [key]: v }));

    return (
        <>
            {/* Stays in view while scrolling through the 15 answers on a phone. */}
            <div className="sticky top-0 z-10 bg-white py-2">
                <div className="flex items-center justify-between bg-stone-100 rounded-lg px-4 py-2">
                    <span className="font-mono font-semibold text-sm sm:text-base text-gray-900">{r.notation}</span>
                    <span className="font-bold text-base sm:text-lg text-gray-900">{r.total ?? "?"}<span className="text-sm font-normal text-gray-600"> / 15</span></span>
                </div>
            </div>

            {ITEMS.map((item) => (
                <div key={item.key} className="p-3 sm:p-4 my-2 sm:my-3 bg-stone-100 rounded-lg">
                    <p className="font-semibold text-sm sm:text-base text-gray-900 mb-2">{item.title} <span className="text-gray-500 font-normal">({item.key})</span></p>
                    <div className="flex flex-col gap-2">
                        {item.options.map((o) => (
                            <OptionButton key={o.score} selected={values[item.key] === o.score} onClick={() => set(item.key, o.score)}
                                badge={o.score} label={o.label} detail={o.detail} />
                        ))}
                        <OptionButton selected={values[item.key] === "NT"} onClick={() => set(item.key, "NT")}
                            badge="NT" label="Non évaluable" detail={item.key === "V" ? "Patient intubé, trachéotomisé ou aphasique" : "Examen impossible (œdème, sédation…)"} />
                    </div>
                </div>
            ))}

            {!r.assessed && (
                <div className="p-6 my-2 sm:my-4 bg-stone-50 border border-dashed border-stone-300 rounded-lg text-center">
                    <p className="text-gray-900 font-semibold text-sm sm:text-base">Cotez les trois critères pour obtenir le score</p>
                    <p className="text-gray-600 text-sm mt-2">Retenez à chaque fois la meilleure réponse obtenue.</p>
                </div>
            )}

            {r.assessed && r.testable && (
                <div className={`p-4 my-2 sm:my-4 rounded-lg w-full bg-gradient-to-b ${SEVERITY_CARD[r.severity.key]} text-white text-center`}>
                    <div className="text-sm opacity-80">Score de Glasgow</div>
                    <div className="text-4xl sm:text-5xl font-bold my-1">{r.total} <span className="text-xl font-semibold">/ 15</span></div>
                    <div className="text-sm font-semibold">{r.severity.label}</div>
                    <div className="text-xs opacity-80 mt-1 font-mono">{r.notation}</div>
                </div>
            )}

            {r.assessed && !r.testable && (
                <div className="p-4 my-2 sm:my-4 rounded-lg w-full bg-stone-100 text-center">
                    <div className="text-sm text-gray-700">Score de Glasgow incomplet</div>
                    <div className="text-2xl font-bold my-1 font-mono text-gray-900">{r.notation}</div>
                    <div className="text-xs text-gray-600">Un critère non évaluable empêche de calculer le total : transmettez le score détaillé.</div>
                </div>
            )}

            {Object.keys(values).length > 0 && (
                <div className="text-center">
                    <button type="button" onClick={() => setValues({})} className="text-sm underline text-gray-700 hover:text-gray-900">Réinitialiser</button>
                </div>
            )}
        </>
    );
}
